import React, { useMemo, useState } from 'react';
import { Boxes, Plus, Trash2, Edit, X, TrendingDown, Loader2 } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { confirmDialog } from '../lib/confirm';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const CATS = ['Machines / Gereedschap', 'Inventaris', 'Hefbruggen / Apparatuur', 'Computers / Software', 'Voertuigen', 'Verbouwing / Pand', 'Overig'];

const leegFormulier = () => ({
  naam: '', categorie: 'Machines / Gereedschap', leverancier: '',
  aanschafDatum: new Date().toISOString().slice(0, 10),
  aanschafwaarde: '', restwaarde: 0, levensduurJaren: 5,
});

export default function VasteActiva() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([carsysApi.getActiva(), carsysApi.getActivaSamenvatting()])
      .then(([it, s]) => { setItems(it); setSum(s); })
      .catch(() => { setItems([]); })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const setF = (upd) => setModal(prev => ({ ...prev, ...upd }));

  const save = async () => {
    if (!modal.naam.trim()) { toast.error(t('Naam is verplicht', 'Naam is verplicht')); return; }
    if (!(Number(modal.aanschafwaarde) > 0)) { toast.error(t('Vul een aanschafwaarde in', 'Vul een aanschafwaarde in')); return; }
    setSaving(true);
    try {
      const payload = { ...modal, aanschafwaarde: Number(modal.aanschafwaarde), restwaarde: Number(modal.restwaarde) || 0, levensduurJaren: Number(modal.levensduurJaren) || 5 };
      if (modal.id) await carsysApi.updateActiva(modal.id, payload);
      else await carsysApi.createActiva(payload);
      toast.success(t('Opgeslagen', 'Opgeslagen'));
      setModal(null); load();
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const verwijder = async (it) => {
    if (!(await confirmDialog(`${t('Activum verwijderen?', 'Activum verwijderen?')} (${it.naam})`))) return;
    try { await carsysApi.deleteActiva(it.id); toast.success(t('Verwijderd', 'Verwijderd')); load(); }
    catch (e) { toast.error(t('Verwijderen mislukt', 'Verwijderen mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  return (
    <div data-testid="vaste-activa-page">
      <div className="cs-page-header">
        <div><h2>{t('Vaste activa & afschrijvingen', 'Vaste activa & afschrijvingen')}</h2><p>{t('Schrijf investeringen zoals hefbruggen, gereedschap en inventaris lineair af', 'Schrijf investeringen zoals hefbruggen, gereedschap en inventaris lineair af')}</p></div>
        <button className="cs-btn primary" data-testid="btn-add-activum" onClick={() => setModal(leegFormulier())}><Plus size={14} /> {t('Activum toevoegen', 'Activum toevoegen')}</button>
      </div>

      <div className="cs-three-col" style={{ marginBottom: 14 }}>
        <div className="cs-kpi" data-testid="kpi-activa-aanschaf"><div className="label">{t('Totale aanschafwaarde', 'Totale aanschafwaarde')}</div><div className="value">{eur(sum?.totaalAanschaf)}</div></div>
        <div className="cs-kpi" data-testid="kpi-activa-boekwaarde"><div className="label">{t('Huidige boekwaarde', 'Huidige boekwaarde')}</div><div className="value" style={{ color: 'var(--cs-accent)' }}>{eur(sum?.totaalBoekwaarde)}</div></div>
        <div className="cs-kpi" data-testid="kpi-activa-afschrijving"><div className="label">{t('Afschrijving dit jaar', 'Afschrijving dit jaar')} · {sum?.jaar}</div><div className="value" style={{ color: '#f59e0b' }}>{eur(sum?.afschrijvingDitJaar)}</div></div>
      </div>

      <div className="cs-card">
        <table className="cs-table">
          <thead><tr>
            <th>{t('Activum', 'Activum')}</th><th>{t('Categorie', 'Categorie')}</th>
            <th>{t('Aanschaf', 'Aanschaf')}</th>
            <th style={{ textAlign: 'right' }}>{t('Aanschafwaarde', 'Aanschafwaarde')}</th>
            <th style={{ textAlign: 'right' }}>{t('Per jaar', 'Per jaar')}</th>
            <th style={{ textAlign: 'right' }}>{t('Boekwaarde', 'Boekwaarde')}</th>
            <th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="cs-empty"><Loader2 size={16} className="animate-spin" style={{ verticalAlign: -3, marginRight: 6 }} />{t('Laden…', 'Laden…')}</td></tr>}
            {!loading && items.map(a => (
              <tr key={a.id} data-testid={`activa-row-${a.id}`}>
                <td style={{ fontWeight: 600 }}>{a.naam}{a.leverancier ? <span style={{ display: 'block', fontSize: 11, color: 'var(--cs-text-muted)', fontWeight: 400 }}>{a.leverancier}</span> : null}</td>
                <td>{a.categorie}</td>
                <td>{a.aanschafDatum ? new Date(a.aanschafDatum).toLocaleDateString('nl-NL') : '—'} <span style={{ color: 'var(--cs-text-muted)', fontSize: 12 }}>· {a.levensduurJaren} {t('jr', 'jr')}</span></td>
                <td style={{ textAlign: 'right' }}>{eur(a.aanschafwaarde)}</td>
                <td style={{ textAlign: 'right', color: '#f59e0b' }}><TrendingDown size={12} style={{ verticalAlign: -1 }} /> {eur(a.afschrijvingPerJaar)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{a.volledigAfgeschreven ? <span className="cs-badge gray">{t('Afgeschreven', 'Afgeschreven')}</span> : eur(a.boekwaarde)}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="cs-btn ghost" style={{ padding: 4 }} data-testid={`activa-edit-${a.id}`} onClick={() => setModal({ ...a })}><Edit size={12} /></button>
                  <button className="cs-btn ghost danger" style={{ padding: 4 }} data-testid={`activa-del-${a.id}`} onClick={() => verwijder(a)}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="cs-empty">{t('Nog geen vaste activa. Voeg investeringen toe (hefbrug, gereedschap, computers) om ze af te schrijven.', 'Nog geen vaste activa. Voeg investeringen toe (hefbrug, gereedschap, computers) om ze af te schrijven.')}</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="cs-modal-overlay" onClick={() => setModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ width: 520, maxWidth: '94vw', background: '#fff', borderRadius: 10, padding: 22, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ marginTop: 0 }}><Boxes size={16} style={{ verticalAlign: -2, marginRight: 6 }} />{modal.id ? t('Activum bewerken', 'Activum bewerken') : t('Activum toevoegen', 'Activum toevoegen')}</h3>
              <button className="cs-btn ghost" onClick={() => setModal(null)} data-testid="activa-close"><X size={16} /></button>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              <div><label className="cs-section-title">{t('Naam', 'Naam')}</label>
                <input className="cs-input" data-testid="activa-naam" placeholder={t('bv. Hefbrug 2-koloms', 'bv. Hefbrug 2-koloms')} value={modal.naam} onChange={e => setF({ naam: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label className="cs-section-title">{t('Categorie', 'Categorie')}</label>
                  <select className="cs-input" data-testid="activa-categorie" value={modal.categorie} onChange={e => setF({ categorie: e.target.value })}>{CATS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><label className="cs-section-title">{t('Leverancier', 'Leverancier')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)' }}>({t('optioneel', 'optioneel')})</span></label>
                  <input className="cs-input" data-testid="activa-leverancier" value={modal.leverancier} onChange={e => setF({ leverancier: e.target.value })} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label className="cs-section-title">{t('Aanschafdatum', 'Aanschafdatum')}</label>
                  <input className="cs-input" type="date" data-testid="activa-datum" value={(modal.aanschafDatum || '').slice(0, 10)} onChange={e => setF({ aanschafDatum: e.target.value })} /></div>
                <div><label className="cs-section-title">{t('Levensduur (jaren)', 'Levensduur (jaren)')}</label>
                  <input className="cs-input" type="number" min="1" max="40" data-testid="activa-levensduur" value={modal.levensduurJaren} onChange={e => setF({ levensduurJaren: e.target.value })} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label className="cs-section-title">{t('Aanschafwaarde (excl. BTW)', 'Aanschafwaarde (excl. BTW)')}</label>
                  <input className="cs-input" type="number" step="0.01" data-testid="activa-aanschafwaarde" value={modal.aanschafwaarde} onChange={e => setF({ aanschafwaarde: e.target.value })} /></div>
                <div><label className="cs-section-title">{t('Restwaarde', 'Restwaarde')}</label>
                  <input className="cs-input" type="number" step="0.01" data-testid="activa-restwaarde" value={modal.restwaarde} onChange={e => setF({ restwaarde: e.target.value })} /></div>
              </div>
              {Number(modal.aanschafwaarde) > 0 && (
                <div style={{ fontSize: 13, padding: '8px 10px', background: 'var(--cs-card-alt)', borderRadius: 6 }} data-testid="activa-preview">
                  {t('Afschrijving per jaar', 'Afschrijving per jaar')}: <b>{eur(Math.max(0, Number(modal.aanschafwaarde) - Number(modal.restwaarde || 0)) / Math.max(1, Number(modal.levensduurJaren || 5)))}</b>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="cs-btn" onClick={() => setModal(null)}>{t('Annuleren', 'Annuleren')}</button>
              <button className="cs-btn primary" data-testid="activa-save" onClick={save} disabled={saving}>{saving ? <Loader2 size={14} className="animate-spin" /> : null} {t('Opslaan', 'Opslaan')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
